/**
 * O que o seletor de unidade do topo mostra na tela atual: as opções, o valor e
 * a cor da unidade (o ponto do seletor, a borda da página e o botão de ajuda).
 *
 * Fora do Financeiro, é o escopo global de sempre (`useTenant`).
 *
 * 🚨 DENTRO DO FINANCEIRO, SÓ AS UNIDADES COM O FINANCEIRO (decisão do PM,
 * 20/10/2026: unidade que não é relativa ao Financeiro da pessoa não aparece
 * para ela). Para administrador e gerente de rede, as opções são só as unidades
 * ligadas dele, e o valor é o escopo efetivo do módulo
 * (`escopoEfetivoDoFinanceiro`): quem escolheu no Funil uma unidade sem o
 * Financeiro vê no seletor a ligada única ou "Todas as unidades", nunca a
 * desligada e nunca o seletor em branco. A escolha global continua a mesma até
 * a pessoa clicar no seletor; de volta ao Funil, ela encontra a unidade que tinha.
 *
 * Para o super_admin, enquanto o acesso carrega e para quem nem entra no
 * Financeiro (o portão da rota fala com essa pessoa), a lista de sempre.
 */

import { useLocation } from "react-router-dom"
import { useTenant } from "@/hooks/useTenant"
import { useAcessoAoFinanceiro } from "@/contexts/AcessoAoFinanceiroContext"
import { escopoEfetivoDoFinanceiro, unidadesDoSeletorNoFinanceiro } from "@/lib/financeiro/acesso"
import type { Tenant } from "@/types/index"

export interface SeletorDoTopo {
  unidades: Tenant[]
  valor: string
  cor: string | null
  /** True dentro do Financeiro com a lista recortada às unidades ligadas. */
  soComFinanceiro: boolean
}

export function rotaDoFinanceiro(pathname: string): boolean {
  return pathname === "/financeiro" || pathname.startsWith("/financeiro/")
}

export function useSeletorDoTopo(): SeletorDoTopo {
  const { tenants, selectedScope, viewAll, activeTenantId, activeTenantColor } = useTenant()
  const { pathname } = useLocation()
  const acesso = useAcessoAoFinanceiro()

  const liberadas =
    rotaDoFinanceiro(pathname) && acesso.estado === "pronto" && acesso.pode ? acesso.unidadesLiberadas : null

  if (liberadas === null) {
    return { unidades: tenants, valor: selectedScope, cor: activeTenantColor, soComFinanceiro: false }
  }

  const efetivo = escopoEfetivoDoFinanceiro(liberadas, { selectedScope, viewAll, activeTenantId })
  const unidade = efetivo.valorDoSeletor === "all" ? null : tenants.find((t) => t.id === efetivo.valorDoSeletor)
  return {
    unidades: unidadesDoSeletorNoFinanceiro(tenants, liberadas),
    valor: efetivo.valorDoSeletor,
    cor: unidade?.primary_color ?? null,
    soComFinanceiro: true,
  }
}
